/**
 * Shared selector resolution utilities.
 *
 * Wraps the try-catch around `document.querySelector` /
 * `document.querySelectorAll` in one place so callers don't duplicate the
 * "invalid admin-configured selector must never break tagging" guard.
 * Admin free-text selectors can throw inside the native selector APIs
 * (syntactically invalid CSS); both UtmLinker.js (observerScopeSelector) and
 * PositionResolver.js (positionContainerSelector) need the same protection.
 */

/**
 * Resolve the FIRST element matching `selector`, returning `fallback` when
 * the selector is empty/invalid or matches nothing.
 *
 * Used by UtmLinker.observeDynamicLinks() to narrow the MutationObserver
 * scope — the fallback there is `document.body` (never breaks link tagging
 * on a bad/empty selector).
 *
 * @param {string|undefined|null} selector CSS selector to resolve.
 * @param {Element|null} [fallback=null] Value returned when the selector
 *   is empty, invalid, or matches nothing.
 * @return {Element|null} The first match, or `fallback`.
 */
export function safeQuerySelector(selector, fallback = null) {
    if (typeof selector !== 'string' || selector.trim() === '') {
        return fallback;
    }
    try {
        const match = document.querySelector(selector);
        return match || fallback;
    } catch (e) {
        console.warn('UTM Linker: safeQuerySelector invalid selector:', selector, e.message);
        return fallback;
    }
}

/**
 * Resolve ALL elements matching `selector` as a real Array, or `null` when
 * the selector is empty/invalid or matches ZERO elements.
 *
 * Preserves PositionResolver.resolvePositionContainers()'s exact semantics:
 * empty/whitespace selector → null, zero matches → null, invalid CSS → null,
 * otherwise a document-order Array (not a live NodeList).
 *
 * @param {string|undefined|null} selector CSS selector to resolve.
 * @return {Array|null} Matched elements in document order, or null.
 */
export function safeQuerySelectorAll(selector) {
    if (typeof selector !== 'string' || selector.trim() === '') {
        return null;
    }
    try {
        const matches = document.querySelectorAll(selector);
        return matches && matches.length > 0 ? Array.from(matches) : null;
    } catch (e) {
        console.warn('UTM Linker: safeQuerySelectorAll invalid selector:', selector, e.message);
        return null;
    }
}
